import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import { PyEntityCreationForm } from '../PyEntityCreationForm';
import { PyTextInput, PySelectInput } from 'components/editor/shared/components/PyMuiComponents';
import { useTranslation } from 'react-i18next';
import { useGetCompanies } from 'pages/editor/useGetCompanies';
import { useLocationData } from 'hooks/useLocationData';
import { ApiListingsRepository } from 'services/api/listings/ApiListingsRepository';
import { validateField, normalizeWebsiteUrl, isFormValid, mapToRequest } from './contactForm';
import type { CreateContactRequest } from 'services/company-and-contacts/entities';

export type { ContactFormData } from 'services/entities/ContactEntity';

const apiListingsRepository = new ApiListingsRepository();

export const initialFormValues: CreateContactRequest = {
  companies: [],
  firstName: '',
  lastName: '',
  email: '',
  useCompanyAddress: false,
};

const CreateContactForm = ({
  onValidityChange,
  onFormDataChange,
  initialValues = initialFormValues,
  existingEmails = [],
}: {
  onValidityChange?: (isValid: boolean) => void;
  onFormDataChange?: (data: CreateContactRequest) => void;
  initialValues?: CreateContactRequest;
  existingEmails?: string[];
}) => {
  const { t } = useTranslation();
  const { data: companiesData = [] } = useGetCompanies();
  const { locationData, getStatesFromCountry } = useLocationData({ listingsRepository: apiListingsRepository });

  const initialCompanyId = initialValues.companies?.[0]?.id ?? '';

  const [selectedCompanyId, setSelectedCompanyId] = useState<string>(initialCompanyId);
  const [useCompanyAddress, setUseCompanyAddress] = useState<boolean>(initialValues.useCompanyAddress ?? false);

  const selectedCompanies = selectedCompanyId ? [{ id: selectedCompanyId }] : [];

  const {
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors, touchedFields },
  } = useForm<Record<string, string>>({
    defaultValues: {
      firstName: initialValues.firstName ?? '',
      lastName: initialValues.lastName ?? '',
      email: initialValues.email ?? '',
      title: initialValues.title ?? '',
      mobileNo: initialValues.mobileNo ?? '',
      officeNo: initialValues.officeNo ?? '',
      websiteUrl: initialValues.websiteUrl ?? '',
      address1: initialValues.address1 ?? '',
      address2: initialValues.address2 ?? '',
      city: initialValues.city ?? '',
      zipCode: initialValues.zipCode ?? '',
      countryId: initialValues.countryId != null ? String(initialValues.countryId) : '',
      stateId: initialValues.stateId != null ? String(initialValues.stateId) : '',
    },
  });

  const formValues = watch();

  const applyValidation = (field: string, value: string) => {
    const { error } = validateField(field, value, t, existingEmails);
    error ? setError(field, { type: 'validate', message: error }) : clearErrors(field);
    return error;
  };

  const notifyParent = (
    companies: CreateContactRequest['companies'],
    companyAddress: boolean,
    validatedField?: string,
    fieldError?: string
  ) => {
    const currentValues = getValues();
    const hasErrors = validatedField
      ? !!fieldError || Object.keys(errors).filter((k) => k !== validatedField).length > 0
      : Object.keys(errors).length > 0;
    onValidityChange?.(isFormValid(currentValues, hasErrors));
    onFormDataChange?.(mapToRequest(currentValues, companies, companyAddress));
  };

  const handleBlur = (field: string, value: string) => {
    const nextValue = field === 'websiteUrl' ? normalizeWebsiteUrl(value) : value;
    setValue(field, nextValue, { shouldTouch: true });
    const error = applyValidation(field, nextValue);
    notifyParent(selectedCompanies, useCompanyAddress, field, error);
  };

  const handleInputChange = (field: string, value: string) => {
    setValue(field, value);
    if (touchedFields[field]) {
      const error = applyValidation(field, value);
      notifyParent(selectedCompanies, useCompanyAddress, field, error);
    } else {
      notifyParent(selectedCompanies, useCompanyAddress);
    }
  };

  const handleCompanyChange = (newCompanyId: string) => {
    setSelectedCompanyId(newCompanyId);
    const newUseCompanyAddress = !!newCompanyId;
    setUseCompanyAddress(newUseCompanyAddress);
    const newCompanies = newCompanyId ? [{ id: newCompanyId }] : [];
    notifyParent(newCompanies, newUseCompanyAddress);
  };

  const handleUseCompanyAddressChange = (checked: boolean) => {
    setUseCompanyAddress(checked);
    notifyParent(selectedCompanies, checked);
  };

  const handleCountryChange = (countryId: string) => {
    setValue('countryId', countryId);
    setValue('stateId', '');
    notifyParent(selectedCompanies, useCompanyAddress);
  };

  const formFields = [
    {
      id: 'firstName',
      label: t('document.create_contact.fields.first_name'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.first_name_placeholder'),
      required: true,
      error: !!errors['firstName'],
      helperText: errors['firstName']?.message || '',
    },
    {
      id: 'lastName',
      label: t('document.create_contact.fields.last_name'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.last_name_placeholder'),
      required: true,
      error: !!errors['lastName'],
      helperText: errors['lastName']?.message || '',
    },
    {
      id: 'title',
      label: t('document.create_contact.fields.title'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.title_placeholder'),
      error: !!errors['title'],
      helperText: errors['title']?.message || '',
    },
    {
      id: 'email',
      label: t('document.create_contact.fields.email'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.email_placeholder'),
      required: true,
      error: !!errors['email'],
      helperText: errors['email']?.message || '',
    },
    {
      id: 'mobileNo',
      label: t('document.create_contact.fields.mobile_no'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.mobile_no_placeholder'),
      error: !!errors['mobileNo'],
      helperText: errors['mobileNo']?.message || '',
    },
    {
      id: 'officeNo',
      label: t('document.create_contact.fields.office_no'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.office_no_placeholder'),
      error: !!errors['officeNo'],
      helperText: errors['officeNo']?.message || '',
    },
    {
      id: 'websiteUrl',
      label: t('document.create_contact.fields.website_url'),
      type: 'text' as const,
      placeholder: t('document.create_contact.fields.website_url_placeholder'),
      error: !!errors['websiteUrl'],
      helperText: errors['websiteUrl']?.message || '',
    },
  ];

  // Filter formValues to only include form field properties (exclude companies, metadata, etc.)
  // This is needed because PyEntityCreationForm only accepts FormValue types (string | number | boolean)
  const formInitialValues = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.id] = (formValues[field.id as keyof typeof formValues] as string | undefined) ?? '';
      return acc;
    }, {} as Record<string, string>);
  }, [formFields, formValues]);

  const companyOptions = companiesData.map((c) => ({ label: c.name ?? '', value: c.id ?? '' }));
  const countryOptions = locationData?.countries.map((c) => ({ label: c.name, value: c.id.toString() })) ?? [];
  const stateOptions = getStatesFromCountry(
    formValues.countryId ? Number(formValues.countryId) : 0,
    locationData?.states ?? [],
    locationData?.countries ?? []
  ).map((s) => ({ label: s.name, value: s.id.toString() }));

  return (
    <Box>
      <PyEntityCreationForm
        formFields={formFields}
        initialValues={formInitialValues}
        onFormDataChange={(newValues) => {
          Object.entries(newValues).forEach(([field, value]) => {
            if (typeof value === 'string') {
              handleInputChange(field, value);
            }
          });
        }}
        textInputValidation={{
          firstName: { onBlur: (value) => handleBlur('firstName', value) },
          lastName: { onBlur: (value) => handleBlur('lastName', value) },
          title: { onBlur: (value) => handleBlur('title', value) },
          email: { onBlur: (value) => handleBlur('email', value) },
          mobileNo: { onBlur: (value) => handleBlur('mobileNo', value) },
          officeNo: { onBlur: (value) => handleBlur('officeNo', value) },
          websiteUrl: { onBlur: (value) => handleBlur('websiteUrl', value) },
        }}
      />
      <Grid container spacing={3.5} sx={{ mt: 0 }}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <PySelectInput
              id="companyId"
              label={t('document.create_contact.fields.company')}
              labelId="companyId"
              value={selectedCompanyId}
              placeholder={t('document.create_contact.fields.company_placeholder')}
              onChange={(e) => handleCompanyChange(e.target.value as string)}
            >
              <MenuItem value="">{t('document.create_contact.fields.company_none')}</MenuItem>
              {companyOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </PySelectInput>
          </FormControl>
          {selectedCompanyId && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t('document.create_contact.company_link_hint')}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={!selectedCompanyId ? t('document.create_contact.fields.company_tooltip_disabled') : ''}>
            <span>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useCompanyAddress}
                    disabled={!selectedCompanyId}
                    onChange={(e) => handleUseCompanyAddressChange(e.target.checked)}
                  />
                }
                label={t('document.create_contact.fields.use_company_address')}
              />
            </span>
          </Tooltip>
        </Grid>
        {!useCompanyAddress && (
          <>
            <Grid item xs={12} sm={6}>
              <PyTextInput
                label={t('document.create_contact.fields.address_line1')}
                placeholder={t('document.create_contact.fields.address_line1_placeholder')}
                value={formValues.address1 || ''}
                onChange={(e) => handleInputChange('address1', e.target.value)}
                onBlur={(e) => handleBlur('address1', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PyTextInput
                label={t('document.create_contact.fields.address_line2')}
                placeholder={t('document.create_contact.fields.address_line2_placeholder')}
                value={formValues.address2 || ''}
                onChange={(e) => handleInputChange('address2', e.target.value)}
                onBlur={(e) => handleBlur('address2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <PySelectInput
                  id="countryId"
                  label={t('document.create_contact.fields.country')}
                  labelId="countryId"
                  value={formValues.countryId || ''}
                  placeholder={t('document.create_contact.fields.country_placeholder')}
                  onChange={(e) => handleCountryChange(e.target.value as string)}
                >
                  {countryOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </PySelectInput>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <PySelectInput
                  id="stateId"
                  label={t('document.create_contact.fields.state')}
                  labelId="stateId"
                  value={formValues.stateId || ''}
                  placeholder={t('document.create_contact.fields.state_placeholder')}
                  disabled={!formValues.countryId}
                  onChange={(e) => {
                    setValue('stateId', e.target.value as string);
                    notifyParent(selectedCompanies, useCompanyAddress);
                  }}
                >
                  {stateOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </PySelectInput>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <PyTextInput
                label={t('document.create_contact.fields.city')}
                placeholder={t('document.create_contact.fields.city_placeholder')}
                value={formValues.city || ''}
                onChange={(e) => handleInputChange('city', e.target.value)}
                onBlur={(e) => handleBlur('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PyTextInput
                label={t('document.create_contact.fields.zip_code')}
                placeholder={t('document.create_contact.fields.zip_code_placeholder')}
                value={formValues.zipCode || ''}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                onBlur={(e) => handleBlur('zipCode', e.target.value)}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default CreateContactForm;
